import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
const ChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    // Strictly typed static responses
    const staticResponses = {
        greeting: {
            role: 'assistant',
            content: "Hello! I'm Ziwatech's repair assistant. I can help with:\n\n• Phone repairs\n• Laptop fixes\n• Gaming consoles\n• Water damage\n• Location info\n\nHow can I help you today?"
        },
        location: {
            role: 'assistant',
            content: "📍 Ziwatech Locations\n\nNORTH YORK (Walk-ins):\nYorkgateMall\n 1 York Gate Blvd\n⏰ Mon-Fri 10AM-8PM | Sat 10AM-6PM\n📞 (647) 200-2040\n\nMISSISSAUGA (Pickup Only):\n📞 Call to arrange: (647) 200-2040"
        },
        phone: {
            role: 'assistant',
            content: "📱 Phone Repair Services\n\n📍 YorkgateMall\n 1 York Gate Blvd\n📞 (647) 200-2040\n\n⚡ Common Services:\n1. Screen replacement ($129-$199)\n2. Battery replacement ($89-$129)\n3. Water damage treatment\n⏱️ Most repairs: 2-3 hours"
        },
        console: {
            role: 'assistant',
            content: "🎮 Gaming Console Repairs\n\n📍 YorkgateMall\n 1 York Gate Blvd\n📞 (647) 200-2040\n\n🛠️ Services:\n1. HDMI port repair\n2. Disc drive replacement\n3. Thermal paste replacement\n4. Software issues\n\n⏱️ Repair time: 3-5 days\n💵 Pricing starts at $79"
        },
        unrelated: {
            role: 'assistant',
            content: "For inquiries about other products or services, please visit our website or contact us at (647) 200-2040."
        },
        error: {
            role: 'assistant',
            content: "For immediate assistance:\n📞 (647) 200-2040\n📍 YorkgateMall\n1 York Gate Blvd\n⏰ Open until 8PM today"
        }
    };
    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    // Initialize with greeting
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([staticResponses.greeting]);
        }
    }, [isOpen]);
    const handleSendMessage = async () => {
        if (!input.trim() || isLoading)
            return;
        // Add user message
        const userMessage = { role: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);
        try {
            const lowerInput = input.toLowerCase();
            // Check for unrelated queries first
            if (/xyz|site|website|other|unrelated/.test(lowerInput)) {
                setMessages(prev => [...prev, staticResponses.unrelated]);
                return;
            }
            // Check for specific queries first
            if (/hi|hello|hey/.test(lowerInput)) {
                setMessages(prev => [...prev, staticResponses.greeting]);
                return;
            }
            if (/location|where|address|store/.test(lowerInput)) {
                setMessages(prev => [...prev, staticResponses.location]);
                return;
            }
            if (/phone|iphone|samsung|screen|battery/.test(lowerInput)) {
                setMessages(prev => [...prev, staticResponses.phone]);
                return;
            }
            if (/console|playstation|ps|xbox|nintendo|switch|gaming/.test(lowerInput)) {
                setMessages(prev => [...prev, staticResponses.console]);
                return;
            }
            if (/sell|buy|purchase|accessory|product/.test(lowerInput)) {
                setMessages(prev => [...prev, staticResponses.unrelated]);
                return;
            }
            // API call for other queries
            const response = await fetch('https://api.deepinfra.com/v1/openai/chat/completions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${process.env.REACT_APP_DEEPINFRA_KEY}`
                },
                body: JSON.stringify({
                    model: "mistralai/Mistral-Small-24B-Instruct-2501",
                    messages: [
                        {
                            role: "system",
                            content: "You are Ziwatech's professional repair assistant. Provide:\n1. Location: 1 York Gate Blvd\n2. Phone: (647) 200-2040\n3. Service details\n4. Price if known\n5. Time estimate\nKeep responses concise."
                        },
                        { role: "user", content: input }
                    ],
                    temperature: 0.2,
                    max_tokens: 150
                })
            });
            // Default response for other repair queries
            setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: "For repair services, please visit our location at YorkgateMall, 1 York Gate Blvd or call (647) 200-2040."
                }]);
        }
        catch (error) {
            setMessages(prev => [...prev, staticResponses.error]);
        }
        finally {
            setIsLoading(false);
        }
    };
    return (_jsx("div", { className: "fixed bottom-6 right-6 z-50 font-sans", children: !isOpen ? (_jsx("button", { onClick: () => setIsOpen(true), className: "bg-blue-700 text-white px-5 py-3 rounded-lg shadow hover:bg-blue-800 transition-colors", children: "Repair Assistance" })) : (_jsxs("div", { className: "bg-white rounded-lg shadow-xl w-96 overflow-hidden border border-gray-200 flex flex-col", style: { maxHeight: '80vh' }, children: [_jsxs("div", { className: "flex items-center justify-between p-4 bg-blue-700 text-white", children: [_jsx("h3", { className: "text-lg font-semibold", children: "Ziwatech Repair Support" }), _jsx("button", { onClick: () => setIsOpen(false), className: "text-white hover:text-gray-200 focus:outline-none text-xl", children: "\u00D7" })] }), _jsxs("div", { className: "flex-1 overflow-y-auto p-4 bg-gray-50", children: [messages.map((message, index) => (message.content ? ( // Only render if content exists
                        _jsx("div", { className: `mb-4 p-3 rounded-lg whitespace-pre-line ${message.role === 'user'
                                ? 'bg-blue-50 ml-8 text-gray-800'
                                : 'bg-white border border-gray-200 mr-8 text-gray-700'}`, children: message.content }, index)) : null)), _jsx("div", { ref: messagesEndRef })] }), _jsx("div", { className: "p-4 border-t bg-white", children: _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { type: "text", value: input, onChange: (e) => setInput(e.target.value), onKeyPress: (e) => e.key === 'Enter' && handleSendMessage(), placeholder: "Ask about repairs...", className: "flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500", disabled: isLoading }), _jsx("button", { onClick: handleSendMessage, className: "bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 disabled:opacity-50", disabled: isLoading, children: isLoading ? '...' : 'Send' })] }) })] })) }));
};
export default ChatWidget;
